import type { DropdownOption } from '../../../components/Dropdown/DropdownOption/DropdownOption';
import type { CustomLink } from '../../../lib/flotiq-client/types/api-response';

import { use, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { postContentObject } from '../../../lib/flotiq-client';
import { updateCTO } from '../../../lib/flotiq-client/api-helpers';
import { parseResponseErrors } from '../../../lib/flotiq-client/response-errors';
import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import Tooltip from '../../../components/Tooltip/Tooltip';

import { useContentObject, useContentTypes } from '../../../hooks/api';
import useSectionSave from '../../../hooks/useSectionSave';
import useSelectedSpace from '../../../hooks/useSelectedSpace';
import useToken from '../../../hooks/useToken';

import { useModals } from '../../../contexts/ModalContext';
import UserContext from '../../../contexts/UserContext';

import { DeleteIcon } from '../../../images/shapes';

import CustomLinkModalContent from '../../../form/CustomLinksModalContent/CustomLinksModalContent';
import SpaceSettingsSection from '../SpaceSettingsSection/SpaceSettingsSection';

const testId = 'custom-links-settings';
const CO_ID = 'flotiq.custom-links';
const SECTION_ID = 'custom-links';

interface CustomLinksSettingsProps {
  contentTypeName: string;
}
const CustomLinksSettings = ({ contentTypeName }: CustomLinksSettingsProps) => {
  const { t } = useTranslation();
  const { permissions } = use(UserContext);
  const jwt = useToken();
  const { space } = useSelectedSpace();
  const modal = useModals();

  const { canDelete, canUpdate, canCreate } = useMemo(
    () => permissions.getCoPermissions(contentTypeName) || {},
    [contentTypeName, permissions],
  );

  const {
    entity: contentObject,
    partialUpdateEntity: partialUpdateContentObject,
    updateEntity: updateContentObject,
    reload,
  } = useContentObject(contentTypeName, CO_ID);

  const ctdParams = useMemo(() => ({ limit: 1000 }), []);
  const { data: ctds } = useContentTypes(ctdParams);

  const ctdOptions: DropdownOption[] = useMemo(
    () =>
      (ctds ?? [])
        .filter((c) => !c.internal)
        .map((c) => ({ label: c.label, value: c.name })),
    [ctds],
  );

  const [links, setLinks] = useState<CustomLink[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (contentObject?.settings) {
      setLinks(contentObject.settings as CustomLink[]);
      setDirty(false);
    }
  }, [contentObject]);

  const openLinkModal = useCallback(
    async (link?: CustomLink) => {
      const result: CustomLink | false = await modal({
        title: (
          <div className="font-bold text-xl sm:text-3xl">
            {t('SpaceSettings.CustomLink')}
          </div>
        ),
        content: (
          <CustomLinkModalContent
            ctdOptions={ctdOptions}
            initialLink={link}
            disabled={!canUpdate && !canCreate}
            testId={testId}
          />
        ),
        size: 'lg',
        buttons: [],
        hideClose: false,
      });

      return result;
    },
    [modal, t, ctdOptions, canUpdate, canCreate],
  );

  const handleAddLink = useCallback(async () => {
    const result = await openLinkModal();
    if (!result) return;
    setLinks((prev) => [...prev, result]);
    setDirty(true);
  }, [openLinkModal]);

  const handleEditLink = useCallback(
    async (index: number) => {
      const result = await openLinkModal(links[index]);
      if (!result) return;
      setLinks((prev) => prev.map((l, i) => (i === index ? result : l)));
      setDirty(true);
    },
    [openLinkModal, links],
  );

  const handleDeleteLink = useCallback((index: number) => {
    setLinks((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (contentObject) {
      const [, errors, hasErrors] = await updateCTO(
        { settings: links },
        contentTypeName,
        permissions.hasCoLimitedFields(contentTypeName)
          ? partialUpdateContentObject
          : updateContentObject,
        t,
        true,
      );
      if (hasErrors) throw errors;
      setDirty(false);
      return;
    }

    const { body, status, ok } = await postContentObject(jwt, space, {
      contentTypeName: contentTypeName,
      id: CO_ID,
      settings: links,
    });
    if (!ok) throw parseResponseErrors(body, status);
    await reload();
    setDirty(false);
  }, [
    contentObject,
    jwt,
    space,
    contentTypeName,
    links,
    reload,
    permissions,
    partialUpdateContentObject,
    updateContentObject,
    t,
  ]);

  useSectionSave(SECTION_ID, handleSave, dirty);

  return (
    <SpaceSettingsSection
      title={t('SpaceSettings.CustomLinks.Title')}
      description={t('SpaceSettings.CustomLinks.Description')}
    >
      {links.length ? (
        <div className="flex flex-col gap-2 pt-6">
          {links.map((link, index) => (
            <div
              key={`${link.link_template}-${link.url_template}`}
              className="flex items-center gap-4.5 md:gap-3"
              {...getTestProps(testId, `link-${index}`)}
            >
              <Button
                buttonColor="borderless"
                additionalClasses={twMerge(
                  'w-full max-w-[752px] !h-12 rounded-lg px-4',
                  'border border-slate-200 bg-slate-50',
                  'hover:bg-blue-50 hover:no-underline',
                  'dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200',
                  'dark:hover:bg-slate-800 dark:hover:text-white',
                )}
                onClick={() => (canUpdate ? handleEditLink(index) : undefined)}
                disabled={!canUpdate}
                {...getTestProps(testId, `link-label-${index}`, 'testId')}
              >
                {link.link_template}
              </Button>

              {canDelete && (
                <Tooltip
                  tooltip={t('Global.Delete')}
                  additionalClasses="inline-block font-normal"
                  tooltipPlacement="topRight"
                >
                  <Button
                    iconImage={
                      <DeleteIcon
                        className={twMerge(
                          'opacity-50 hover:opacity-100',
                          'text-indigo-750 dark:text-slate-200',
                        )}
                      />
                    }
                    buttonColor="borderless"
                    additionalIconClasses="w-4.5 md:w-3.5"
                    onClick={() => handleDeleteLink(index)}
                    {...getTestProps(testId, `delete-link-${index}`, 'testId')}
                  />
                </Tooltip>
              )}
            </div>
          ))}
        </div>
      ) : undefined}

      {canCreate && (
        <div className="mt-6">
          <Button
            onClick={handleAddLink}
            {...getTestProps(testId, 'add-link', 'testId')}
          >
            {t('SpaceSettings.CustomLinks.AddLink')}
          </Button>
        </div>
      )}
    </SpaceSettingsSection>
  );
};

export default CustomLinksSettings;
